
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

export type TablePresentationMode = 'minimal' | 'striped' | 'cards';

@Component({
  selector: 'app-data-table',
  imports: [CommonModule, MatIconModule, MatTableModule, MatTooltipModule, MatButtonModule],
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fade', [
      state('void', style({ opacity: 0 })),
      transition(':enter', [animate('300ms ease-in', style({ opacity: 1 }))]),
    ]),
    trigger('rowAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ],
})
export class DataTableComponent {
  @Input() data: any[] = [];
  @Input() columns: {
    key: string;
    header: string;
    isDetails?: boolean;
    fields?: string[];
    classes?: string[];
    isAction?: boolean;
    actionIcon?: string;
    actionClass?: string;
    activeActionIcon?: string;
    activeActionClass?: string;
    tooltip?: string;
    sortable?: boolean;
  }[] = [];
  @Input() displayedColumns: string[] = [];
  @Input() selectedItem: any | null = null;
  @Input() compact = false;
  @Input() stickyHeader = true;
  @Input() emptyMessage?: string;
  @Input() sortColumn?: string;
  @Input() sortDirection?: 'asc' | 'desc' = 'asc';
  @Input() presentationMode: TablePresentationMode = 'minimal';

  @Output() rowClicked = new EventEmitter<any>();
  @Output() actionClicked = new EventEmitter<any>();
  @Output() sortChanged = new EventEmitter<{ column: string, direction: 'asc' | 'desc' }>();

  // Example
  // const columns = [
  //   { key: 'description', header: 'Details', isDetails: true, fields: ['name', 'description'], classes: ["name-col", "description-col"] },
  //   { key: 'action', header: '', isAction: true, actionIcon: 'chat_bubble_outline', activeActionIcon: 'chat_bubble' },
  // ];


  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if the columns input has changed
    if (changes['columns'] && this.columns?.length > 0) {
      this.displayedColumns = this.columns.map((column) => column.key);
    }
  }

  isSelected(row: any): boolean {
    if (!this.selectedItem) {
      return false;
    }
    // Check if the row matches the selected item
    if (this.selectedItem === row || this.selectedItem === row.workspace_id || this.selectedItem === row.id) {
      return true;
    }
    return false;
  }

  clickAction(event: any, row: any): void {
    event.stopPropagation();
    event.preventDefault();
    this.actionClicked.emit({ event, row });
  }

  clickRow(event: any, row: any): void {
    this.rowClicked.emit({ event, row });
  }

  onSort(column: any): void {
    if (!column.sortable) return;

    const newDirection = this.sortColumn === column.key && this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.sortColumn = column.key;
    this.sortDirection = newDirection;

    this.sortChanged.emit({ column: column.key, direction: newDirection });
  }

  getSortIcon(column: any): string {
    if (this.sortColumn !== column.key) {
      return 'unfold_more';
    }
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  getActionIcon(row: any, column: any): string {
    return this.isSelected(row)
      ? column.activeActionIcon || column.actionIcon || 'chat_bubble'
      : column.actionIcon || 'chat_bubble_outline';
  }
}
